import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NavigationStart, Router, Event } from '@angular/router';
import { StatefulService } from '@app/service/stateful.service';
import {
  LoginWidgetContextData,
  OIDLoginWidgetExportData,
  LoginWidgetStateInfo,
  EaAuthenticateModule,
  EaAuthenticateComponent
} from '@cudirect/ea-login';
import { JwtToken, TokenService, SharedUiLibraryModule } from '@cudirect/shared-ui-library';
import { SawDust } from '@cudirect/sawdust/sawdust';
import { GoogleTrackingService } from '@shared/service/google-tracking.service';
import { UserSessionService } from '@shared/service/user-session.service';
import { XApiKeyService } from '@shared/service/x-api-key.service';
import { LoginService } from '@user/service/login/login.service';
import { filter, Subscription } from 'rxjs';

@Component({
  selector: 'dx-verify',
  templateUrl: './verify.component.html',
  standalone: true,
  imports: [EaAuthenticateModule, SharedUiLibraryModule, EaAuthenticateComponent]
})
export class VerifyComponent implements OnInit, OnDestroy {
  // Inject dependencies using the new inject pattern
  private readonly logger = inject(SawDust);
  private readonly loginService = inject(LoginService);
  private readonly tokenService = inject(TokenService);
  private readonly router = inject(Router);
  private readonly userService = inject(UserSessionService);
  private readonly xApiKeyService = inject(XApiKeyService);
  private readonly statefulService = inject(StatefulService);
  private readonly googleTrackingService = inject(GoogleTrackingService);

  @Input() userID: string;
  @Input() method: 'Email_Code' | 'Phone';
  // Defaulting this functionality to be disabled. Maybe this should be on all the time,
  // but we need to be sure. Currently, only the silent verification uses this.
  @Input() allowCloseableModal = false;
  @Output() success = new EventEmitter<OIDLoginWidgetExportData>();
  @Input() isPrimaryApplicant: boolean = true;
  @Input() isYouthMembership: boolean = false;
  public widgetContext: LoginWidgetContextData;
  // Displays the login widget if true
  public showWidget = false;
  // Displays the error modal if true
  public showError = false;
  // Displays the retry button if true
  public showRetry = false;
  // Displays the button loader animation if true
  public loading = false;
  // Holder for subscriptions
  public subscription: Subscription;
  // This gets toggled for invalid phone number so the modal can be closed and the number changed.
  public showCloseable = false;

  ngOnInit(): void {
    this.subscribeToPopstateEvent();
    this.loginService.getWidgetContext('verifyMFA').then((res) => {
      this.widgetContext = res;
      this.widgetContext.oid.dxMode = false; // Set DX mode to false
      // TESTING ***
      // this.userID = 'MFATestUser@origence.testinator.com';
      // this.method = 'Email_Code'; // Email_Code or Phone
      // END TESTING ***
      this.widgetContext.oid.verifyUserId = this.userID;
      // Override token cache name to avoid conflicts when is not primary applicant
      // In a youth membership the primary applicant is not required to verify identity
      if(!this.isPrimaryApplicant && !this.isYouthMembership) {
        this.widgetContext.config.tokenCacheNameOverride = 'secondaryApplicantMfaTokenCache';
      }

      if (!this.tokenService.isInitialized) {
        this.tokenService
          .initialize(this.widgetContext.oid.apiBaseURL, this.xApiKeyService.createApiHeader(), this.widgetContext.oid.oidConfigName, true)
          .then(() => {
            this.sendCode();
          });
      } else {
        this.sendCode();
      }
    });
  }

  /**
   * Captures browser back button click and navigate to login page
   */
  private subscribeToPopstateEvent() {
    this.subscription = this.router.events
      .pipe(
        filter((event: Event) => {
          return event instanceof NavigationStart;
        })
      )
      .subscribe((event: NavigationStart) => {
        // - popstate (ie, browser controlled change such as Back button).
        if (event.navigationTrigger === 'popstate') {
          this.userService.logOut(true);
          this.router.navigate(['user/login']);
        }
      });
  }

  ngOnDestroy(): void {
    this.subscription && this.subscription.unsubscribe();
  }

  // Starts passwordless login. This will send an mfa code
  public sendCode(): void {
    this.showError = false;

    this.tokenService.startPasswordless(this.userID, this.method, true).then((res) => {
      this.loading = false;
      this.showRetry = false;
      if (res.isSuccess !== false) {
        // Code has been sent.
        this.showError = false;
        this.showWidget = true;
      } else {
        res.data = res.data as HttpErrorResponse;
        // Check if phone number was invalid
        if (res.data?.error?.message === 'StartPasswordlessInvalidPhoneNumber' && this.allowCloseableModal) {
          this.showError = true;
          this.showCloseable = true;
        } else {
          // All other errors
          this.showError = true;
          this.showRetry = true;
        }
      }
    });
  }

  public receivedDataHandler(data: OIDLoginWidgetExportData | string): void {
    data = data as OIDLoginWidgetExportData;
    this.showWidget = false;
    if (data.statusMsg === 'Authenticated') {
      this.googleTrackingService.publishEvent('arcdx.silent-verify-success', {});
      // SUCCESS!
      if(this.isPrimaryApplicant) {
        this.tokenService.updateToken(data.token as JwtToken);
      }
      this.success.emit(data);
      // Continue with the success path handling here...
    } else {
      // Handle error. Should only get here if APIs are down.
      this.logger.info('Received data handler error block', { component: 'VerifyComponent' });
      this.showError = true;
      this.showRetry = false;
    }
  }

  public receivedStateInfoHandler(data: LoginWidgetStateInfo | string): void {
    data = data as LoginWidgetStateInfo;
    if (data?.event === 'timeout' && data?.code === 'sendToLogin') {
      this.router.navigate(['/login']);
    }
  }

  // Handler for clicking the retry button.
  public retry(): void {
    this.loading = true;
    this.sendCode();
  }

  // Closes the error modal.
  public closeAlert(): void {
    this.statefulService.wrap('ss:showMFAVerification').value = false;
    this.showError = false;
    this.showRetry = false;
    this.loading = false;
    this.showWidget = false;
    // Navigating to the same route to trigger the save and continue button to be enabled.
    this.router.navigate([this.router.url]);
  }
}
